import React, { PureComponent } from 'react';
import { func, object, string, bool } from 'prop-types';
import { Link, Switch, Route, Redirect } from 'react-router-dom';
import { injectIntl, intlShape } from 'react-intl';
import { TextField, Box } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { Button } from '../../components/Button';
import PasswordInput from '../../components/PasswordInput';
import LogoLink from '../LogoLink';
import { history } from '../../store';
import { AUTH_ERROR_TYPES } from '../../constants/Values';

class ResetPassword extends PureComponent {
  static propTypes = {
    validationErrors: object,
    resetPasswordStatus: string,
    loader: bool,
    intl: intlShape.isRequired,
    match: object.isRequired,
    resetAllAuthErrorMessages: func,
    userResetPassword: func,
    userResetPasswordStatus: func,
    userSetNewPassword: func,
  };

  static defaultProps = {
    resetAllAuthErrorMessages: () => {},
    userResetPassword: () => {},
    userResetPasswordStatus: () => {},
    userSetNewPassword: () => {},
  };

  state = {
    login: null,
    code: null,
    password: null,
  };

  componentDidUpdate() {
    const { resetPasswordStatus } = this.props;

    if (resetPasswordStatus) {
      this.props.userResetPasswordStatus();
    }
  }

  componentWillUnmount() {
    this.props.resetAllAuthErrorMessages();
  }

  handleResetPassword = e => {
    e.preventDefault();
    const { login } = this.state;

    this.props.userResetPassword(login);
  };

  handleSetNewPassword = e => {
    e.preventDefault();
    const { login, code, password } = this.state;

    this.props.userSetNewPassword(login, code, password);
  };

  handleBackToResetForm = () => {
    const { match } = this.props;

    history.push(match.url);
  };

  renderResetPasswordForm = () => {
    const { intl, validationErrors } = this.props;
    const errorMessage = validationErrors[AUTH_ERROR_TYPES.IDENTIFIER];

    return (
      <div className="app-login-content">
        <Box className="app-login-header" sx={{ mb: 0.5 }}>
          <h1>{intl.formatMessage({ id: 'appModule.resetPassword' })}</h1>
        </Box>

        <div className="app-login-form">
          <form>
            <fieldset>
              <TextField
                autoFocus
                sx={{ mt: { xs: 0.5, sm: 2 }, mb: { xs: 1, sm: 2 } }}
                defaultValue={this.state.login}
                error={!!errorMessage}
                fullWidth
                helperText={intl.formatMessage({
                  id: errorMessage || 'auth.identifier.helperText',
                })}
                label={intl.formatMessage({ id: 'auth.identifier.label' })}
                onChange={event => this.setState({ login: event.target.value })}
                margin="normal"
              />

              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Link to="/signin">{intl.formatMessage({ id: 'appModule.signIn' })}</Link>

                <Button
                  size="large"
                  labelId="appModule.reset"
                  variant="contained"
                  color="primary"
                  disabled={!this.state.login}
                  onClick={this.handleResetPassword}
                />
              </Box>
            </fieldset>
          </form>
        </div>
      </div>
    );
  };

  renderResetPasswordConfirm = () => {
    const { intl, match, validationErrors } = this.props;
    const { login } = this.state;

    if (!login) {
      return <Redirect to={match.url} />;
    }

    const codeErrorMessage = validationErrors[AUTH_ERROR_TYPES.VERIFICATION_CODE];
    const passErrorMessage = validationErrors[AUTH_ERROR_TYPES.NEW_PASS];

    return (
      <div className="app-login-content">
        <Box className="app-login-header" sx={{ mb: 0.5 }}>
          <h1>{intl.formatMessage({ id: 'appModule.resetPassword' })}</h1>
        </Box>

        <div className="app-login-form">
          <form>
            <fieldset>
              <TextField
                autoFocus
                sx={{ mt: { xs: 0.5, sm: 2 }, mb: { xs: 1, sm: 2 } }}
                defaultValue={this.state.code}
                error={!!codeErrorMessage}
                fullWidth
                helperText={codeErrorMessage ? intl.formatMessage({ id: codeErrorMessage }) : null}
                label={intl.formatMessage({ id: 'appModule.code' })}
                margin="normal"
                onChange={event => this.setState({ code: event.target.value })}
              />

              <a href="#" onClick={this.handleResetPassword}>
                {intl.formatMessage({ id: 'appModule.resendCode' })}
              </a>

              <PasswordInput
                id="password"
                defaultValue={this.state.password}
                error={!!passErrorMessage}
                helperText={
                  passErrorMessage ||
                  this.props.intl.formatMessage({ id: 'auth.newPassword.helperText' })
                }
                label={intl.formatMessage({
                  id: passErrorMessage ? 'auth.errors.wrongNewPassword' : 'appModule.newPassword',
                })}
                margin="normal"
                sx={{ mt: { xs: 1, sm: 3 }, mb: { xs: 1, sm: 3 } }}
                onChange={event => this.setState({ password: event.target.value })}
              />

              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button
                  size="large"
                  labelId="appModule.back"
                  variant="text"
                  onClick={this.handleBackToResetForm}
                />

                <Button
                  size="large"
                  labelId="appModule.reset"
                  variant="contained"
                  color="primary"
                  onClick={this.handleSetNewPassword}
                  disabled={!this.state.code || !this.state.password}
                />
              </Box>
            </fieldset>
          </form>
        </div>
      </div>
    );
  };

  render() {
    const { match, resetPasswordStatus, loader } = this.props;

    switch (resetPasswordStatus) {
      case 'pending':
        return <Redirect to={`${match.url}/confirm`} />;
      case 'success':
        return <Redirect to="/signin" />;
      default:
        return (
          <Box className="app-login-container" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="app-login-main-content">
              <Box className="app-logo-content" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <LogoLink />
              </Box>

              <Switch>
                <Route exact path={match.url} render={this.renderResetPasswordForm} />
                <Route path={`${match.url}/confirm`} render={this.renderResetPasswordConfirm} />
              </Switch>
            </div>

            {loader && (
              <div className="loader-view">
                <CircularProgress />
              </div>
            )}
          </Box>
        );
    }
  }
}

export default injectIntl(ResetPassword);
